import React, { useEffect, useMemo } from 'react'
import { EMPTY_VALUE_TEXT } from '../../../_constants/text'
import { useLazyCoreFetch } from '@mv-submodules/inplant-core-fe'
import { getSpeedAbsorptionRatioSummary } from './_api'
import Row from '@mv-submodules/inplant-components-fe/ui/components/Grid/Row'
import { Loader } from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/shared'
import { ChartPluginComponentProps } from '../../../_types'

/**
 * Formats a numeric value for display in the speed absorption ratio summary table.
 * @param value - The numeric value to format, or null if no data is available.
 * @returns A formatted string representation of the value with 4 decimal places, or '-' if the value is null.
 */
const _formatValueOrFallback = (value: number | null | undefined) => {
  if (value == null) {
    return EMPTY_VALUE_TEXT
  }
  return value.toFixed(4)
}

const SpeedAbsorptionSummary = (props: ChartPluginComponentProps) => {
  const { assetId, plant, t } = props
  const [fetchData, { data, loading, error }] = useLazyCoreFetch(getSpeedAbsorptionRatioSummary)

  const abortController = useMemo(() => new AbortController(), [])

  useEffect(() => {
    fetchData({ assetId, plant, periods: ['1d', '7d', '30d', '150d', '365d'] }, { abortController })
    return () => {
      abortController.abort()
    }
  }, [assetId, plant])

  if (loading) {
    return (
      <Row>
        <Loader />
      </Row>
    )
  }

  if (error || !data) {
    return <div className="alert alert-danger w-100 col-sm-6 mx-auto">{t('plantAnalysis.fetchErrors')}</div>
  }

  return (
    <div className={'row row-legend row-legend-bottom'}>
      <div className="col-md-10">
        <h5>{t('plantAnalysis.speedAbsorptionRatioSummary.title')}</h5>
        <table className="table table-striped table-sm">
          <caption>{t('plantAnalysis.speedAbsorptionRatioSummary.caption')}</caption>
          <thead>
            <tr>
              <th>{t('plantAnalysis.speedAbsorptionRatioSummary.period.title')}</th>
              <th>{t('plantAnalysis.speedAbsorptionRatioSummary.meanAbsorption.title')}</th>
              <th>{t('plantAnalysis.speedAbsorptionRatioSummary.meanSpeed.title')}</th>
              <th>{t('plantAnalysis.speedAbsorptionRatioSummary.speedAbsorptionRatio.title')}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data).map(([period, values]) => (
              <tr key={period}>
                <td>{t(`plantAnalysis.speedAbsorptionRatioSummary.periods.${period}`)}</td>
                <td>{_formatValueOrFallback(values?.meanAbsorption)}</td>
                <td>{_formatValueOrFallback(values?.meanSpeed)}</td>
                <td>{_formatValueOrFallback(values?.speedAbsorptionRatio)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
export default SpeedAbsorptionSummary
